import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  SaveDraftCart,
  LoadDraftCart,
  GetDraftCartsList,
  ClearDraftCart,
  DeleteDraftCart,
  UpdateDraftCartStatus,
} from "../Api";
import { ServiceQueryOptions, unwrapApiResponse } from "./query_helpers";
import { LOOKUP_STALE_TIME } from "../Constant/AppConstants";

export const draftCartQueryKeys = {
  draftCart: () => ["draftCart"] as const,
  draftCartsList: () => ["draftCartsList"] as const,
};

export const invalidateDraftCartQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["draftCart"] });
  queryClient.invalidateQueries({ queryKey: ["draftCartsList"] });
};

export const useDraftCartQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: draftCartQueryKeys.draftCart(),
    queryFn: async () => unwrapApiResponse(await LoadDraftCart()),
  });
};

export const useDraftCartRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: draftCartQueryKeys.draftCart(),
        queryFn: async () => unwrapApiResponse(await LoadDraftCart()),
      }),
  });
};

export const useDraftCartsListQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: draftCartQueryKeys.draftCartsList(),
    queryFn: async () => unwrapApiResponse(await GetDraftCartsList()),
  });
};

export const useDraftCartsListRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: draftCartQueryKeys.draftCartsList(),
        queryFn: async () => unwrapApiResponse(await GetDraftCartsList()),
      }),
  });
};

export const useSaveDraftCartMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof SaveDraftCart>[0]) =>
      SaveDraftCart(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateDraftCartQueries(queryClient);
      }
    },
  });
};

export const useClearDraftCartMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => ClearDraftCart(),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateDraftCartQueries(queryClient);
      }
    },
  });
};

export const useDeleteDraftCartMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      id: Parameters<typeof DeleteDraftCart>[0];
      is_checkout?: Parameters<typeof DeleteDraftCart>[1];
    }) => DeleteDraftCart(payload.id, payload.is_checkout),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateDraftCartQueries(queryClient);
      }
    },
  });
};

export const useUpdateDraftCartStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      id: Parameters<typeof UpdateDraftCartStatus>[0];
      order_status_id: Parameters<typeof UpdateDraftCartStatus>[1];
    }) => UpdateDraftCartStatus(payload.id, payload.order_status_id),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateDraftCartQueries(queryClient);
      }
    },
  });
};

export const useDraftCartService = (config?: {
  fetchDraftCartEnabled?: boolean;
  fetchDraftCartsListEnabled?: boolean;
}) => {
  const draftCartQuery = useDraftCartQuery({
    enabled: config?.fetchDraftCartEnabled !== false,
  });

  const draftCartsListQuery = useDraftCartsListQuery({
    enabled: config?.fetchDraftCartsListEnabled !== false,
  });

  const saveDraftCartMutation = useSaveDraftCartMutation();
  const clearDraftCartMutation = useClearDraftCartMutation();
  const deleteDraftCartMutation = useDeleteDraftCartMutation();
  const updateDraftCartStatusMutation = useUpdateDraftCartStatusMutation();

  return {
    draftCartQuery,
    draftCartsListQuery,
    saveDraftCartMutation,
    clearDraftCartMutation,
    deleteDraftCartMutation,
    updateDraftCartStatusMutation,
  };
};
